// ──────────────────────────────────────────────────────────────────────────
// DiarioStatisticheConfrontoStagioni.js — Sotto-tab "🔄 Confronto stagioni"
//
// v132: implementazione completa.
// - Dropdown stagioni con scorciatoie (Ultime 2/3/Tutte) + selezione manuale
// - Card ROI per stagione + card Rendimento composto (TWR) — nascosta se 1 stagione
// - Equity line normalizzata (%) su asse temporale relativo (0 → 1)
// - Tabella riassuntiva con riga "Aggregato"
// - Banner "1 sola stagione" se selezionata 1
// ──────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { fmtEur, fmtPct, fmtNum } from "../components/stats/statsFormat";
import SpiegaPopover from "../components/stats/SpiegaPopover";

import { API } from "../config";

// Palette colori per le curve dell'equity line — stesso pattern già usato altrove nell'app.
const PALETTE = [
  "#0d8a4e", // verde
  "#1d4ed8", // blu
  "#9d174d", // bordeaux
  "#c2410c", // arancio
  "#7e22ce", // viola
  "#0f766e", // teal
  "#1f2a37", // grigio scuro
  "#b45309", // oro
];

const colorePerIndice = (i) => PALETTE[i % PALETTE.length];

// Chiave sessionStorage per persistenza selezione stagioni (intra-sessione).
const SS_KEY = "statistiche_confronto_stagioni";

export default function DiarioStatisticheConfrontoStagioni() {
  // ── Stati: lista stagioni disponibili + selezione corrente
  const [stagioniDisponibili, setStagioniDisponibili] = useState([]);
  const [scorciatoia, setScorciatoia] = useState(() => {
    return sessionStorage.getItem(SS_KEY + "_scorciatoia") || "ultime3";
  });
  const [selezioneManuale, setSelezioneManuale] = useState(() => {
    const raw = sessionStorage.getItem(SS_KEY + "_manuale");
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  });
  const [dropdownAperto, setDropdownAperto] = useState(false);

  const [datiConfronto, setDatiConfronto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState(null);

  const dropdownRef = useRef(null);

  // ── Persistenza in sessionStorage
  useEffect(() => {
    sessionStorage.setItem(SS_KEY + "_scorciatoia", scorciatoia);
  }, [scorciatoia]);
  useEffect(() => {
    sessionStorage.setItem(SS_KEY + "_manuale", JSON.stringify(selezioneManuale));
  }, [selezioneManuale]);

  // ── Carico lista stagioni disponibili (al mount)
  useEffect(() => {
    fetch(`${API}/diario/stagioni`)
      .then((r) => r.json())
      .then((d) => {
        // ordino cronologicamente decrescente (più recente in alto, D103)
        const ordinate = (d || []).slice().sort((a, b) => {
          return (b.data_inizio || "").localeCompare(a.data_inizio || "");
        });
        setStagioniDisponibili(ordinate);
      })
      .catch(() => setStagioniDisponibili([]));
  }, []);

  // ── Calcolo IDs effettivi in base a scorciatoia o selezione manuale
  const idsSelezionati = (() => {
    if (scorciatoia === "manuale") return selezioneManuale;
    if (stagioniDisponibili.length === 0) return [];
    if (scorciatoia === "ultime2") return stagioniDisponibili.slice(0, 2).map((s) => s.id);
    if (scorciatoia === "ultime3") return stagioniDisponibili.slice(0, 3).map((s) => s.id);
    if (scorciatoia === "tutte") return stagioniDisponibili.map((s) => s.id);
    return [];
  })();

  // ── Carico dati Confronto quando cambiano gli ids selezionati
  const caricaConfronto = useCallback(async () => {
    if (!idsSelezionati || idsSelezionati.length === 0) {
      setDatiConfronto(null);
      return;
    }
    setLoading(true);
    setErrore(null);
    try {
      const qs = `stagione_ids=${idsSelezionati.join(",")}`;
      const r = await fetch(`${API}/diario/statistiche/confronto?${qs}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDatiConfronto(d);
    } catch (e) {
      setErrore(e.message || "Errore di rete");
    } finally {
      setLoading(false);
    }
  }, [idsSelezionati.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    caricaConfronto();
  }, [caricaConfronto]);

  // ── Click fuori dropdown per chiuderlo
  useEffect(() => {
    if (!dropdownAperto) return;
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownAperto(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [dropdownAperto]);

  // ── Etichetta intelligente del pulsante chiuso del dropdown
  const etichettaPulsante = (() => {
    if (scorciatoia === "ultime2") return "Ultime 2 stagioni";
    if (scorciatoia === "ultime3") return "Ultime 3 stagioni";
    if (scorciatoia === "tutte") return "Tutte le stagioni";
    if (scorciatoia === "manuale") {
      const nomi = stagioniDisponibili
        .filter((s) => selezioneManuale.includes(s.id))
        .map((s) => s.etichetta);
      if (nomi.length === 0) return "Nessuna selezionata";
      if (nomi.length === 1) return `1 stagione: ${nomi[0]}`;
      return `${nomi.length} stagioni: ${nomi.join(", ")}`;
    }
    return "Stagioni da confrontare";
  })();

  // ── Toggle selezione manuale checkbox
  const toggleManuale = (id) => {
    setSelezioneManuale((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  // ── Render
  if (stagioniDisponibili.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔄</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>
          Confronto stagioni
        </div>
        <div style={{ fontSize: 13, maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
          Non ci sono stagioni configurate. Crea una stagione dalla tab Capitale per iniziare.
        </div>
      </div>
    );
  }

  const stagioniKpi = datiConfronto?.stagioni || [];
  const aggregato = datiConfronto?.aggregato || null;
  const unaSolaStagione = stagioniKpi.length === 1;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Dropdown stagioni */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280",
                      textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
          Stagioni da confrontare
        </div>
        <div ref={dropdownRef} style={{ position: "relative", display: "inline-block", minWidth: 280 }}>
          <button
            onClick={() => setDropdownAperto((v) => !v)}
            style={{
              width: "100%", padding: "10px 14px",
              border: "1px solid #d1d5db", borderRadius: 8,
              background: "#fff", textAlign: "left",
              fontSize: 14, fontFamily: "inherit",
              cursor: "pointer", display: "flex", justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {etichettaPulsante}
            </span>
            <span style={{ marginLeft: 8, color: "#6b7280" }}>▼</span>
          </button>
          {dropdownAperto && (
            <DropdownPannello
              stagioni={stagioniDisponibili}
              scorciatoia={scorciatoia}
              setScorciatoia={(s) => { setScorciatoia(s); setDropdownAperto(false); }}
              selezioneManuale={selezioneManuale}
              toggleManuale={toggleManuale}
              applicaManuale={() => { setScorciatoia("manuale"); setDropdownAperto(false); }}
              chiudi={() => setDropdownAperto(false)}
            />
          )}
        </div>
      </div>

      {/* Banner 1 sola stagione */}
      {unaSolaStagione && (
        <div style={{
          background: "#f1f5f9", border: "1px solid #e2e8f0",
          borderRadius: 8, padding: "10px 14px", marginBottom: 18,
          fontSize: 13, color: "#475569", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span>ℹ️</span>
          <span>
            Stai visualizzando 1 sola stagione. Aggiungi stagioni dal dropdown per confrontarle.
          </span>
        </div>
      )}

      {/* Stato di caricamento / errore */}
      {loading && (
        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>
          Caricamento confronto…
        </div>
      )}
      {errore && (
        <div style={{ padding: 16, color: "#9b2c2c", background: "#fed7d7", borderRadius: 8 }}>
          Errore: {errore}
        </div>
      )}

      {/* Card stagioni + card TWR (TWR nascosta se 1 sola) */}
      {!loading && !errore && stagioniKpi.length > 0 && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: `repeat(${stagioniKpi.length + (unaSolaStagione ? 0 : 1)}, 1fr)`,
            gap: 12, marginBottom: 24,
          }}>
            {stagioniKpi.map((s, i) => (
              <CardStagione key={s.id} stagione={s} colore={colorePerIndice(i)} />
            ))}
            {!unaSolaStagione && aggregato && (
              <CardTWR aggregato={aggregato} nStagioni={stagioniKpi.length} />
            )}
          </div>

          {/* Equity line normalizzata */}
          <EquityNormalizzata stagioni={stagioniKpi} />

          {/* Tabella riassuntiva */}
          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2a37", marginBottom: 10 }}>
              Tabella riassuntiva
            </div>
            <TabellaRiassuntiva stagioni={stagioniKpi} aggregato={aggregato} unaSola={unaSolaStagione} />
          </div>
        </>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Dropdown pannello: scorciatoie + selezione manuale checkbox
// ──────────────────────────────────────────────────────────────────────────
function DropdownPannello({
  stagioni, scorciatoia, setScorciatoia,
  selezioneManuale, toggleManuale, applicaManuale, chiudi,
}) {
  return (
    <div style={{
      position: "absolute", top: "calc(100% + 4px)", left: 0,
      width: 280, background: "#fff",
      border: "1px solid #d1d5db", borderRadius: 8,
      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      padding: 8, zIndex: 100,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280",
                    textTransform: "uppercase", letterSpacing: 0.5,
                    padding: "8px 8px 4px" }}>
        Scorciatoie
      </div>
      {[
        { id: "ultime2", label: "Ultime 2" },
        { id: "ultime3", label: "Ultime 3" },
        { id: "tutte", label: "Tutte" },
      ].map((opt) => (
        <button
          key={opt.id}
          onClick={() => setScorciatoia(opt.id)}
          style={{
            display: "block", width: "100%", padding: "8px 10px",
            background: scorciatoia === opt.id ? "#dbeafe" : "transparent",
            color: scorciatoia === opt.id ? "#1e40af" : "#1f2a37",
            border: "none", borderRadius: 6, textAlign: "left",
            fontSize: 14, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          {opt.label}{scorciatoia === opt.id ? " ✓" : ""}
        </button>
      ))}

      <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280",
                    textTransform: "uppercase", letterSpacing: 0.5,
                    padding: "12px 8px 4px", borderTop: "1px solid #f1f5f9", marginTop: 6 }}>
        Selezione manuale
      </div>
      {stagioni.map((s) => (
        <label
          key={s.id}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 10px", cursor: "pointer", fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={selezioneManuale.includes(s.id)}
            onChange={() => toggleManuale(s.id)}
          />
          <span>{s.etichetta}{s.data_fine ? "" : " (in corso)"}</span>
        </label>
      ))}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8,
                    padding: "10px 8px 4px", borderTop: "1px solid #f1f5f9", marginTop: 6 }}>
        <button
          onClick={chiudi}
          style={{
            padding: "6px 14px", background: "#fff", color: "#1f2a37",
            border: "1px solid #d1d5db", borderRadius: 6,
            fontSize: 13, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Annulla
        </button>
        <button
          onClick={applicaManuale}
          disabled={selezioneManuale.length === 0}
          style={{
            padding: "6px 14px",
            background: selezioneManuale.length === 0 ? "#cbd5e1" : "#1d4ed8",
            color: "#fff", border: "none", borderRadius: 6,
            fontSize: 13, cursor: selezioneManuale.length === 0 ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          Applica
        </button>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Card di una singola stagione
// ──────────────────────────────────────────────────────────────────────────
function CardStagione({ stagione, colore }) {
  const positivo = stagione.roi_pct >= 0;
  const coloreValore = positivo ? "#0d8a4e" : "#c53030";
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 12, padding: 14,
      borderLeft: `4px solid ${colore}`,
    }}>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4,
                    display: "flex", alignItems: "center", gap: 6 }}>
        <span>Stagione {stagione.etichetta}</span>
        {stagione.in_corso && (
          <span style={{
            fontSize: 10, fontWeight: 600,
            background: "#dbeafe", color: "#1e40af",
            padding: "2px 6px", borderRadius: 4,
          }}>
            in corso
          </span>
        )}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: coloreValore, lineHeight: 1.1 }}>
        {fmtPct(stagione.roi_pct)}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
        {fmtEur(stagione.pl_eur)} su {fmtEur(stagione.capitale_iniziale_eur, 0)}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Card Rendimento composto (TWR) — stile evidenziato bordo blu
// ──────────────────────────────────────────────────────────────────────────
function CardTWR({ aggregato, nStagioni }) {
  const positivo = aggregato.roi_pct_twr >= 0;
  const coloreValore = positivo ? "#0d8a4e" : "#c53030";
  return (
    <div style={{
      background: "#f0f9ff", border: "2px solid #1d4ed8",
      borderRadius: 12, padding: 14,
    }}>
      <div style={{ fontSize: 12, color: "#1e40af", marginBottom: 4,
                    display: "flex", alignItems: "center", gap: 6 }}>
        <span>Rendimento composto</span>
        <SpiegaPopover
          titolo="Rendimento composto (TWR)"
          formula="Time-Weighted Return: (1+ROI₁) × (1+ROI₂) × ... − 1. È lo standard del settore investimenti per calcolare il rendimento di portafogli multi-periodo, indipendente dai versamenti e prelievi tra una stagione e l'altra."
          dettagli={`Calcolato sulle ${nStagioni} stagioni selezionate. Valore positivo: il metodo nel suo complesso ha generato profitto. Valore negativo: il metodo ha generato perdita. È coerente al 100% con il modo in cui vengono comunicate le performance nei fondi di investimento e nelle strategie di trading.`}
        />
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: coloreValore, lineHeight: 1.1 }}>
        {fmtPct(aggregato.roi_pct_twr)}
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
        su {nStagioni} stagioni
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Equity line normalizzata (%) — più curve sovrapposte
// ──────────────────────────────────────────────────────────────────────────
function EquityNormalizzata({ stagioni }) {
  const W = 800;  // logico
  const H = 280;
  const padding = { top: 30, right: 20, bottom: 40, left: 50 };
  const plotW = W - padding.left - padding.right;
  const plotH = H - padding.top - padding.bottom;

  // Calcolo min/max per scaling Y
  let minPct = 0, maxPct = 0;
  for (const s of stagioni) {
    for (const p of s.equity_normalizzata || []) {
      if (p.pct < minPct) minPct = p.pct;
      if (p.pct > maxPct) maxPct = p.pct;
    }
  }
  // Margine 10% sopra/sotto
  const range = Math.max(Math.abs(minPct), Math.abs(maxPct), 5);
  minPct = Math.min(minPct, -range * 0.1);
  maxPct = Math.max(maxPct, range * 0.1);

  const xScale = (t) => padding.left + t * plotW;
  const yScale = (pct) => padding.top + plotH - ((pct - minPct) / (maxPct - minPct)) * plotH;
  const yZero = yScale(0);

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 12, padding: 18, position: "relative", isolation: "isolate", zIndex: 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2a37" }}>
          Equity line normalizzata (%)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
          <span>Asse temporale comune</span>
          <SpiegaPopover
            titolo="Asse temporale comune"
            formula="L'asse orizzontale rappresenta il tempo relativo della stagione (0 = inizio, 1 = fine), non date assolute."
            dettagli="Le stagioni vengono allineate all'inizio per permettere un confronto visivo dell'andamento percentuale, indipendentemente dalle date di calendario. L'asse verticale è la percentuale rispetto al capitale di inizio stagione: 0% = capitale iniziale, +10% = 10% sopra il capitale iniziale, −10% = 10% sotto."
          />
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
           style={{ width: "100%", height: "auto", maxHeight: 280, display: "block" }}>
        {/* Asse Y: linea zero */}
        <line x1={padding.left} x2={W - padding.right} y1={yZero} y2={yZero}
              stroke="#9ca3af" strokeDasharray="3,3" strokeWidth="1" />
        <text x={padding.left - 8} y={yZero + 4} fontSize="11" fill="#6b7280" textAnchor="end">0</text>

        {/* Etichette Y in alto e in basso */}
        <text x={padding.left - 8} y={padding.top + 4} fontSize="11" fill="#6b7280" textAnchor="end">
          {fmtPct(maxPct, 0)}
        </text>
        <text x={padding.left - 8} y={padding.top + plotH + 4} fontSize="11" fill="#6b7280" textAnchor="end">
          {fmtPct(minPct, 0)}
        </text>

        {/* Etichette X */}
        <text x={padding.left} y={H - 12} fontSize="11" fill="#6b7280" textAnchor="start">
          inizio stagione
        </text>
        <text x={padding.left + plotW / 2} y={H - 12} fontSize="11" fill="#6b7280" textAnchor="middle">
          metà stagione
        </text>
        <text x={W - padding.right} y={H - 12} fontSize="11" fill="#6b7280" textAnchor="end">
          fine stagione
        </text>

        {/* Curve per ogni stagione */}
        {stagioni.map((s, i) => {
          const punti = s.equity_normalizzata || [];
          if (punti.length < 2) return null;
          const path = punti.map((p, idx) => {
            const x = xScale(p.t);
            const y = yScale(p.pct);
            return `${idx === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
          }).join(" ");
          return (
            <path key={s.id} d={path}
                  fill="none" stroke={colorePerIndice(i)} strokeWidth="2" />
          );
        })}
      </svg>

      {/* Legenda */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12,
                    marginTop: 12, fontSize: 12, color: "#475569" }}>
        {stagioni.map((s, i) => (
          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 16, height: 3, background: colorePerIndice(i), display: "inline-block" }}></span>
            <span>
              Stagione {s.etichetta}{s.in_corso ? " in corso" : ""} ({fmtPct(s.roi_pct)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Tabella riassuntiva
// ──────────────────────────────────────────────────────────────────────────
function TabellaRiassuntiva({ stagioni, aggregato, unaSola }) {
  const cellaSx = { padding: "12px 14px", textAlign: "left", fontSize: 13, background: "#fff" };
  const cellaDx = { padding: "12px 14px", textAlign: "right", fontSize: 13, background: "#fff" };
  const headerSx = { padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6b7280",
                     borderBottom: "1px solid #e5e7eb", textTransform: "uppercase",
                     letterSpacing: 0.3, background: "#fff" };
  const headerDx = { padding: "12px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#6b7280",
                     borderBottom: "1px solid #e5e7eb", textTransform: "uppercase",
                     letterSpacing: 0.3, background: "#fff" };

  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
      overflowX: "auto", position: "relative", isolation: "isolate", zIndex: 5,
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "inherit", background: "#fff" }}>
        <colgroup>
          {/* Stagione | Capitale | P/L | ROI | Op | Win rate | Drawdown | Slippage */}
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "17%" }} />
        </colgroup>
        <thead>
          <tr>
            <th style={headerSx}>Stagione</th>
            <th style={headerDx}>Capitale iniziale</th>
            <th style={headerDx}>P/L €</th>
            <th style={headerDx}>ROI %</th>
            <th style={headerDx}>Op.</th>
            <th style={headerDx}>Win rate</th>
            <th style={headerDx}>
              <span
                title="Drawdown massimo (€) — Picco massimo del capitale di stagione meno la valle successiva, in euro. È la peggior perdita consecutiva attraversata durante la stagione. Il numero in euro è univoco e rappresenta il dolore massimo subito. Per il dato in percentuale (vs picco o vs capitale iniziale), apri il sotto-tab Sintesi."
                style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}
              >
                Drawdown max (€)
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 14, height: 14, borderRadius: "50%",
                  background: "#e5e7eb", color: "#6b7280",
                  fontSize: 9, fontWeight: 700, fontStyle: "italic",
                }}>i</span>
              </span>
            </th>
            <th style={headerDx}>
              <span
                title="Slippage quote cumulativo (€) — È il vero costo (o guadagno) in euro che il book ti ha generato rispetto alle quote del segnale del sistema. Negativo: il book ti è andato contro. Positivo: ti ha favorito. Come si calcola: per ogni operazione chiusa con entrambe le quote registrate, confronto il P/L netto che avresti ottenuto con la quota teorica del sistema con quello effettivo della quota di book; sommo le differenze sulle operazioni della stagione. Esempio: una Punta vincente con stake 10 €, quota teorica 2,10, quota effettiva 2,05 → ti è costata 0,49 € (al netto delle fee). Su una Punta persa lo slippage in euro è 0 — la quota non incide sulla perdita."
                style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "help" }}
              >
                Slippage cum. (€)
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 14, height: 14, borderRadius: "50%",
                  background: "#e5e7eb", color: "#6b7280",
                  fontSize: 9, fontWeight: 700, fontStyle: "italic",
                }}>i</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {stagioni.map((s) => (
            <tr key={s.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <td style={cellaSx}>
                <span>{s.etichetta}</span>
                {s.in_corso && (
                  <span style={{
                    marginLeft: 6, fontSize: 10, fontWeight: 600,
                    background: "#dbeafe", color: "#1e40af",
                    padding: "2px 6px", borderRadius: 4,
                  }}>
                    in corso
                  </span>
                )}
              </td>
              <td style={cellaDx}>{fmtEur(s.capitale_iniziale_eur, 0)}</td>
              <td style={{ ...cellaDx, color: s.pl_eur >= 0 ? "#0d8a4e" : "#c53030" }}>
                {fmtEur(s.pl_eur)}
              </td>
              <td style={{ ...cellaDx, color: s.roi_pct >= 0 ? "#0d8a4e" : "#c53030" }}>
                {fmtPct(s.roi_pct)}
              </td>
              <td style={cellaDx}>{fmtNum(s.n_op, 0)}</td>
              <td style={cellaDx}>{s.win_rate_pct === null ? "—" : fmtPct(s.win_rate_pct, 1)}</td>
              <td style={{ ...cellaDx, color: "#c53030" }}>{fmtNum(-Math.abs(s.drawdown_max_eur), 2)}</td>
              <td style={{ ...cellaDx, color: s.slippage_cumulativo_eur === null ? "#94a3b8" :
                                                (s.slippage_cumulativo_eur >= 0 ? "#0d8a4e" : "#c53030") }}>
                {s.slippage_cumulativo_eur === null ? "—" : fmtNum(s.slippage_cumulativo_eur, 2)}
              </td>
            </tr>
          ))}
          {!unaSola && aggregato && (
            <tr style={{ borderTop: "2px solid #cbd5e1", fontWeight: 600 }}>
              <td style={cellaSx}>Aggregato</td>
              <td style={cellaDx}>—</td>
              <td style={{ ...cellaDx, color: aggregato.pl_eur >= 0 ? "#0d8a4e" : "#c53030" }}>
                {fmtEur(aggregato.pl_eur)}
              </td>
              <td style={{ ...cellaDx, color: aggregato.roi_pct_twr >= 0 ? "#0d8a4e" : "#c53030" }}>
                {fmtPct(aggregato.roi_pct_twr)} <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>(TWR)</span>
              </td>
              <td style={cellaDx}>{fmtNum(aggregato.n_op, 0)}</td>
              <td style={cellaDx}>{aggregato.win_rate_pct === null ? "—" : fmtPct(aggregato.win_rate_pct, 1)}</td>
              <td style={{ ...cellaDx, color: "#c53030" }}>
                {fmtNum(-Math.abs(aggregato.drawdown_max_eur), 2)}
              </td>
              <td style={{ ...cellaDx, color: aggregato.slippage_cumulativo_eur === null ? "#94a3b8" :
                                                (aggregato.slippage_cumulativo_eur >= 0 ? "#0d8a4e" : "#c53030") }}>
                {aggregato.slippage_cumulativo_eur === null ? "—" : fmtNum(aggregato.slippage_cumulativo_eur, 2)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
